import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { BranchProvider } from "@/context/BranchContext";
import { queryClient, QueryClientProvider } from "@/lib/queryClient";
import SmartNotificationSystem from "@/components/SmartNotificationSystem";
import GlobalSearchShortcut from "@/components/GlobalSearchShortcut";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { firstAllowedRouteBy } from "@/lib/permissionRoutes";
import React, { Suspense } from "react";
import PageSkeleton from "@/components/PageSkeleton";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";

// Lazy load ALL pages for instant navigation
const Dashboard = React.lazy(() => import("@/pages/Dashboard"));
const TableMap = React.lazy(() => import("@/pages/TableMap"));
const OrderScreen = React.lazy(() => import("@/pages/OrderScreen"));
const Kitchen = React.lazy(() => import("@/pages/Kitchen"));
const Billing = React.lazy(() => import("@/pages/Billing"));
const PaymentScreen = React.lazy(() => import("@/pages/PaymentScreen"));
const CashRegister = React.lazy(() => import("@/pages/CashRegister"));
const Settings = React.lazy(() => import("@/pages/settings"));
const PrinterSettings = React.lazy(() => import("@/pages/settings/PrinterSettings"));
const InventoryManager = React.lazy(() => import("@/pages/InventoryManager"));
const Suppliers = React.lazy(() => import("@/pages/Suppliers"));
const Reports = React.lazy(() => import("@/pages/Reports"));
const Customers = React.lazy(() => import("@/pages/Customers"));
const KitchenTV = React.lazy(() => import("@/pages/KitchenTV"));
const Reservations = React.lazy(() => import("@/pages/Reservations"));
const ProductConfig = React.lazy(() => import("@/pages/ProductConfig"));
const UserConfig = React.lazy(() => import("@/pages/UserConfig"));
const AnulacionesReport = React.lazy(() => import("@/pages/AnulacionesReport"));
const TicketDemo = React.lazy(() => import("@/pages/TicketDemo"));
const Help = React.lazy(() => import("@/pages/Help"));
const BillHistory = React.lazy(() => import("@/pages/BillHistory"));
const LoyaltyCard = React.lazy(() => import("@/pages/LoyaltyCard"));
const SuperAdmin = React.lazy(() => import("@/pages/SuperAdmin"));
const Audit = React.lazy(() => import("@/pages/Audit"));
const Accounting = React.lazy(() => import("@/pages/Accounting"));
// 🔒 TALLER-VEHICLES-FE-2026-09-11 — Modulo de taller (feature flag "taller")
const Vehicles = React.lazy(() => import("@/pages/Vehicles"));
const ServiceOrders = React.lazy(() => import("@/pages/ServiceOrders"));  // 🔒 TALLER-ORDERS-FE-2026-09-11
const Pay = React.lazy(() => import("@/pages/Pay"));
const PaySuccess = React.lazy(() => import("@/pages/PaySuccess"));
const PayCancel = React.lazy(() => import("@/pages/PayCancel"));

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center bg-background"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// 🔒 BUG-F1 / AUTH-GUARDS-2026-05 — RoleGate guards a specific route by permission or admin level.
// Falls back to a SAFE default route (first allowed) if the user lacks the required permission.
// The child route itself is still wrapped by ProtectedRoute (auth check) at the
// parent level, so user is guaranteed to be set here.
function _firstAllowedRoute(hasPermission, isAdmin) {
  // 🔒 AUTH-GUARDS-2026-05 Phase 5: single source of truth shared with Login.js
  // to avoid post-login flickering (/tables → /kitchen) for kitchen role.
  return firstAllowedRouteBy(hasPermission, isAdmin);
}

function RoleGate({ permission, anyPermission, adminOnly = false, children }) {
  const { user, hasPermission, isAdmin } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin) return <Navigate to={_firstAllowedRoute(hasPermission, isAdmin)} replace />;
  // Single permission check
  if (permission && !hasPermission(permission) && !isAdmin) {
    return <Navigate to={_firstAllowedRoute(hasPermission, isAdmin)} replace />;
  }
  // Any-of-permissions check (for screens with multiple tabs each with own permission)
  if (Array.isArray(anyPermission) && anyPermission.length > 0 && !isAdmin) {
    const hasAny = anyPermission.some(p => hasPermission(p));
    if (!hasAny) return <Navigate to={_firstAllowedRoute(hasPermission, isAdmin)} replace />;
  }
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/kitchen-tv" element={<Suspense fallback={<PageSkeleton />}><KitchenTV /></Suspense>} />
      <Route path="/loyalty-card/:customerId" element={<Suspense fallback={<PageSkeleton />}><LoyaltyCard /></Suspense>} />
      {/* 🔒 PAYPAL-FASE4-2026-05 — rutas públicas de pago (sin auth JWT). */}
      <Route path="/pay" element={<Suspense fallback={<PageSkeleton />}><Pay /></Suspense>} />
      <Route path="/pay/success" element={<Suspense fallback={<PageSkeleton />}><PaySuccess /></Suspense>} />
      <Route path="/pay/cancel" element={<Suspense fallback={<PageSkeleton />}><PayCancel /></Suspense>} />
      {/* Root redirect: if loading show spinner, if no user go to login, else go to dashboard */}
      <Route path="/" element={
        loading ? (
          <div className="h-screen flex items-center justify-center bg-background">
            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : !user ? (
          <Navigate to="/login" replace />
        ) : (
          <Navigate to="/dashboard" replace />
        )
      } />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="dashboard" element={<RoleGate permission="view_dashboard"><Suspense fallback={<PageSkeleton />}><Dashboard /></Suspense></RoleGate>} />
        <Route path="tables" element={<RoleGate anyPermission={['open_table','access_all_tables','move_tables','manage_tables']}><Suspense fallback={<PageSkeleton />}><TableMap /></Suspense></RoleGate>} />
        <Route path="order/:tableId" element={<RoleGate anyPermission={['open_table','add_products']}><Suspense fallback={<PageSkeleton />}><OrderScreen /></Suspense></RoleGate>} />
        <Route path="order/quick/:orderId" element={<RoleGate anyPermission={['open_table','add_products']}><Suspense fallback={<PageSkeleton />}><OrderScreen /></Suspense></RoleGate>} />
        <Route path="kitchen" element={<RoleGate anyPermission={['send_kitchen','receive_orders']}><Suspense fallback={<PageSkeleton />}><Kitchen /></Suspense></RoleGate>} />
        <Route path="billing/:orderId" element={<RoleGate permission="create_bill"><Suspense fallback={<PageSkeleton />}><Billing /></Suspense></RoleGate>} />
        <Route path="payment/:billId" element={<RoleGate permission="collect_payment"><Suspense fallback={<PageSkeleton />}><PaymentScreen /></Suspense></RoleGate>} />
        <Route path="cash-register" element={<RoleGate permission="access_caja"><Suspense fallback={<PageSkeleton />}><CashRegister /></Suspense></RoleGate>} />
        <Route path="inventory" element={<Navigate to="/inventory-manager" replace />} />
        <Route path="inventory-manager" element={<RoleGate anyPermission={['manage_inventory','manage_purchase_orders','receive_purchase_orders','manage_suppliers','manage_ingredients','manage_warehouses','manage_recipes','manage_subrecipes','check_stock','view_inventory_valuation','view_inventory_audit','manage_stock_counts']}><Suspense fallback={<PageSkeleton />}><InventoryManager /></Suspense></RoleGate>} />
        <Route path="suppliers" element={<RoleGate permission="manage_suppliers"><Suspense fallback={<PageSkeleton />}><Suppliers /></Suspense></RoleGate>} />
        <Route path="reports" element={<RoleGate permission="view_reports"><Suspense fallback={<PageSkeleton />}><Reports /></Suspense></RoleGate>} />
        <Route path="reports/anulaciones" element={<RoleGate permission="view_reports"><Suspense fallback={<PageSkeleton />}><AnulacionesReport /></Suspense></RoleGate>} />
        <Route path="reports/facturas" element={<RoleGate permission="view_reports"><Suspense fallback={<PageSkeleton />}><BillHistory /></Suspense></RoleGate>} />
        <Route path="customers" element={<RoleGate permission="manage_customers"><Suspense fallback={<PageSkeleton />}><Customers /></Suspense></RoleGate>} />
        <Route path="reservations" element={<RoleGate permission="manage_reservations"><Suspense fallback={<PageSkeleton />}><Reservations /></Suspense></RoleGate>} />
        <Route path="settings" element={<RoleGate anyPermission={['config_sistema','config_users','config_mesas','config_ventas','config_productos','config_inventario','config_impresion','config_estacion','config_reportes','config_clientes','config_impuestos','config_ncf','config_apariencia','config_descuentos','view_system_logs','manage_products','manage_users','manage_tables','manage_areas']}><Suspense fallback={<PageSkeleton />}><Settings /></Suspense></RoleGate>} />
        <Route path="settings/printer" element={<RoleGate permission="config_impresion"><Suspense fallback={<PageSkeleton />}><PrinterSettings /></Suspense></RoleGate>} />
        <Route path="product/:productId" element={<RoleGate permission="manage_products"><Suspense fallback={<PageSkeleton />}><ProductConfig /></Suspense></RoleGate>} />
        <Route path="user/:userId" element={<RoleGate permission="manage_users"><Suspense fallback={<PageSkeleton />}><UserConfig /></Suspense></RoleGate>} />
        <Route path="ticket-demo" element={<Suspense fallback={<PageSkeleton />}><TicketDemo /></Suspense>} />
        <Route path="help" element={<Suspense fallback={<PageSkeleton />}><Help /></Suspense>} />
        <Route path="super-admin" element={<RoleGate adminOnly><Suspense fallback={<PageSkeleton />}><SuperAdmin /></Suspense></RoleGate>} />
        <Route path="audit" element={<RoleGate adminOnly><Suspense fallback={<PageSkeleton />}><Audit /></Suspense></RoleGate>} />
        <Route path="accounting" element={<RoleGate permission="access_accounting"><Suspense fallback={<PageSkeleton />}><Accounting /></Suspense></RoleGate>} />
        {/* 🔒 TALLER-VEHICLES-FE-2026-09-11 — La pantalla se autoprotege: si la
            flag "taller" esta apagada el backend responde 403 y muestra el
            aviso de modulo no habilitado. No lleva RoleGate de permiso porque
            el permiso de taller todavia no existe. */}
        <Route path="vehicles" element={<Suspense fallback={<PageSkeleton />}><Vehicles /></Suspense>} />
        {/* 🔒 TALLER-ORDERS-FE-2026-09-11 — se autoprotege igual que Vehiculos */}
        <Route path="service-orders" element={<Suspense fallback={<PageSkeleton />}><ServiceOrders /></Suspense>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <BranchProvider>
            <BrowserRouter>
              <AppRoutes />
              <GlobalSearchShortcut />
              <SmartNotificationSystem />
              {/* 🔒 SONNER-TOASTER-FIX-2026-05 — montaje del Toaster de sonner.
                  Sin esto, todas las llamadas a toast.success/error de sonner
                  eran silenciosas (la librería ejecutaba pero no había
                  componente renderizando los toasts en el DOM). Esto causaba
                  que el botón "Guardar" del modal de plan en Super Admin
                  pareciera no hacer nada — el toast.error('Nombre requerido')
                  se llamaba pero nadie lo veía. */}
              <SonnerToaster position="top-right" richColors closeButton />
            </BrowserRouter>
          </BranchProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
